/**
 * Form state for the Profile Form, and the payload builders that turn it into
 * what POST profile/saveAll expects.
 *
 * Key shapes are transcribed from `ProfileForm.SaveAll`
 * (wwwroot/js/app/profile/form.js:221-545). Where that code sent a field, this
 * sends the same field with the same name and the same type — including the
 * ones the upstream probably ignores, because we cannot see the upstream.
 */

/** DTO/Enum-less mappings the original hardcoded by element id. */
export const GENDER = { Male: 1, Female: 2 } as const

/**
 * `mstMaritalStatusID`, mapped from the radio's element id in SaveAll:
 * cbSingle -> 1, cbMarried -> 2, cbDivorced -> 3, anything else (cbWidow) -> 4.
 * Note the order does NOT match the on-screen order, which is
 * Single / Divorced / Married / Widow.
 */
export const MARITAL_STATUS = { Single: 1, Married: 2, Divorced: 3, Widow: 4 } as const

/** The fixed list in _Personalinformation.cshtml. Values are the stored strings. */
export const JOB_PREFERENCES = [
  { value: 'IT', label: 'Information Technology' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Marketing', label: 'Marketing' },
  { value: 'HR', label: 'Human Resources' },
  { value: 'Sales', label: 'Sales' },
  { value: 'Operations', label: 'Operations' },
]

export interface PersonalInfoState {
  FullName: string
  Email: string
  PhoneNumber: string
  PlaceOfBirth: string
  DateOfBirth: string
  Age: string
  /** ProvinceID. Sent as State_HCIS_ID, which SaveAll requires to be numeric. */
  DomicileId: string
  /**
   * The province's display name. SaveAll sent BOTH the id (as State_HCIS_ID)
   * and the selected option's text (as `Domicile`), so the name is carried in
   * state rather than re-derived at submit time.
   */
  DomicileName: string
  /** City_HCIS_ID. Also required to be numeric. */
  CityId: string
  CountryCode: string
  Address: string
  /** The stored value, e.g. "IT". */
  JobPreferences: string
  /** The option's text, e.g. "Information Technology" — what SaveAll actually sent. */
  JobPreferencesLabel: string
  ReligionCode: string
  Gender: number
  MaritalStatusId: number
  LinkedIn: string
  Instagram: string
}

export interface EducationRow {
  mstDegreeID: string
  DegreeName: string
  mstUniversityID: string
  UniversityName: string
  UniversityOther: string
  mstMajorID: string
  MajorName: string
  MajorOther: string
  GPA: string
  MonthIn: string
  YearIn: string
  MonthOut: string
  YearOut: string
  isUntilNow: boolean
}

export interface WorkExperienceRow {
  Company: string
  mstIndustryID: string
  IndustryName: string
  Position: string
  mstWorkExperienceLevelID: string
  LevelName: string
  MonthIn: string
  YearIn: string
  MonthOut: string
  YearOut: string
  isUntilNow: boolean
  Salary: string
  JobDescription: string
}

export interface OrganizationRow {
  OrganizationName: string
  mstOrganizationTypeID: string
  OrganizationTypeName: string
  /** 1 = Leader, 2 = Member, per the hardcoded <option>s in _Organization.cshtml. */
  OrganizationPosition: string
}

export interface AchievementState {
  IsHaveAchievement: boolean
  AchievementDescription: string
  SoftSkillDescription: string
  TechnicalSkill: string
}

/**
 * `null` means "not answered yet".
 *
 * The Razor put `checked="checked"` on the No radio, so these arrived
 * pre-answered — which made the completeness tracker either count a section the
 * candidate had never looked at, or force them to toggle Yes and back to No just
 * to register an answer. Starting unanswered removes both problems: picking
 * either option completes the section straight away.
 *
 * At submit time an unanswered value still goes upstream as `false`, which is
 * exactly what the pre-checked No radio used to send.
 */
export interface FamilyState {
  IsHaveFamilyInTBG: boolean | null
  FamilyInTBG: string
  IsHaveFamilyInTelcoIndustry: boolean | null
  FamilyInTelcoIndustry: string
}

export interface MedicalState {
  IsHaveMedicalHistory: boolean | null
  MedicalHistory: string
}

/** "yyyy-MM" from a month input -> the MonthIn/YearIn pair the DTOs use. */
export function splitMonthValue(value: string): { month: number | null; year: number | null } {
  const match = /^(\d{4})-(\d{2})$/.exec(value)
  if (!match) return { month: null, year: null }
  return { year: Number(match[1]), month: Number(match[2]) }
}

export function joinMonthValue(year: number | null, month: number | null): string {
  if (!year || !month) return ''
  return `${year}-${String(month).padStart(2, '0')}`
}

/**
 * Builds the multipart body SaveAll posted.
 *
 * `Email` is still sent inside each DTO because the upstream DTOs declare it —
 * but the BFF overwrites every one of them with the session's address before
 * forwarding, so what the client puts here cannot select another account.
 */
export function buildSaveAllForm(input: {
  personal: PersonalInfoState
  education: EducationRow[]
  work: WorkExperienceRow[]
  organizations: OrganizationRow[]
  achievement: AchievementState
  family: FamilyState
  medical: MedicalState
  photo: File | null
  cv: File | null
  certificate: File | null
}): FormData {
  const form = new FormData()
  const email = input.personal.Email
  const now = new Date().toISOString()

  if (input.cv) form.append('CurriculumVitaePath', input.cv)
  if (input.photo) form.append('PhotoPath', input.photo)
  if (input.certificate) form.append('CertificationPath', input.certificate)

  form.append(
    'trxPersonalInformation',
    JSON.stringify({
      FullName: input.personal.FullName || '',
      Email: email || '',
      PlaceOfBirth: input.personal.PlaceOfBirth || '',
      DateOfBirth: input.personal.DateOfBirth || '',
      Gender: input.personal.Gender,
      PhoneNumber: input.personal.PhoneNumber || '',
      LinkedIn: input.personal.LinkedIn || '',
      Instagram: input.personal.Instagram || '',
      mstMaritalStatusID: input.personal.MaritalStatusId,
      // Left blank on purpose: the BFF fills these in with the paths the
      // upstream returns after storing the uploaded files.
      PhotoPath: '',
      CurriculumVitaePath: '',
      Age: Number.parseInt(input.personal.Age, 10) || 0,
      Address: input.personal.Address || '',
      Domicile: input.personal.DomicileName || '',
      JobPreferences: input.personal.JobPreferencesLabel || '',
      Country_HCIS_Code: input.personal.CountryCode || '',
      ReligionCode: input.personal.ReligionCode || '',
      City_HCIS_ID: input.personal.CityId || '',
      State_HCIS_ID: input.personal.DomicileId || '',
    }),
  )

  form.append(
    'trxCandidateEducation',
    JSON.stringify(
      input.education.map((row) => ({
        trxCandidatePersonalInformationID: null,
        mstDegreeID: row.mstDegreeID ? Number.parseInt(row.mstDegreeID, 10) : null,
        DegreeName: row.DegreeName || '',
        Email: email,
        mstUniversityID: row.mstUniversityID ? Number.parseInt(row.mstUniversityID, 10) : null,
        UniversityName: row.UniversityName || '',
        UniversityOther: row.UniversityOther || '',
        mstMajorID: row.mstMajorID ? Number.parseInt(row.mstMajorID, 10) : null,
        MajorName: row.MajorName || '',
        MajorOther: row.MajorOther || '',
        EducationStatus: null,
        Semester: 0,
        GPA: row.GPA ? Number.parseFloat(row.GPA) : null,
        MonthIn: row.MonthIn ? Number.parseInt(row.MonthIn, 10) : null,
        YearIn: row.YearIn ? Number.parseInt(row.YearIn, 10) : null,
        MonthOut: row.MonthOut ? Number.parseInt(row.MonthOut, 10) : null,
        YearOut: row.YearOut ? Number.parseInt(row.YearOut, 10) : null,
        isUntilNow: row.isUntilNow,
        PeriodeIn: '',
        PeriodeOut: '',
      })),
    ),
  )

  /**
   * SaveAll sent a single all-blank row rather than `[]` when the candidate had
   * no work experience — and the same for organisations. Reproduced: the
   * upstream evidently tolerates it, and an empty array is a different input
   * whose handling we cannot see.
   */
  const workRows =
    input.work.length > 0
      ? input.work.map((row) => ({
          trxCandidatePersonalInformationID: null,
          Email: email,
          Company: row.Company || '',
          Position: row.Position || '',
          mstIndustryID: row.mstIndustryID ? Number.parseInt(row.mstIndustryID, 10) : null,
          mstWorkExperienceLevelID: row.mstWorkExperienceLevelID
            ? Number.parseInt(row.mstWorkExperienceLevelID, 10)
            : null,
          JobDescription: row.JobDescription || '',
          Salary: row.Salary || '',
          MonthIn: row.MonthIn ? Number.parseInt(row.MonthIn, 10) : null,
          YearIn: row.YearIn ? Number.parseInt(row.YearIn, 10) : null,
          MonthOut: row.MonthOut ? Number.parseInt(row.MonthOut, 10) : null,
          YearOut: row.YearOut ? Number.parseInt(row.YearOut, 10) : null,
          isUntilNow: row.isUntilNow,
          Achievements: '',
        }))
      : [
          {
            trxCandidatePersonalInformationID: null,
            Email: email,
            Company: '',
            Position: '',
            mstIndustryID: null,
            mstWorkExperienceLevelID: null,
            JobDescription: '',
            Salary: '',
            MonthIn: null,
            YearIn: null,
            MonthOut: null,
            YearOut: null,
            isUntilNow: false,
            Achievements: '',
          },
        ]
  form.append('trxCandidateWorkExperience', JSON.stringify(workRows))

  const orgRows =
    input.organizations.length > 0
      ? input.organizations.map((row) => ({
          trxCandidatePersonalInformationID: null,
          Email: email,
          OrganizationName: row.OrganizationName || '',
          mstOrganizationTypeID: row.mstOrganizationTypeID
            ? Number.parseInt(row.mstOrganizationTypeID, 10)
            : null,
          OrganizationPosition: row.OrganizationPosition
            ? Number.parseInt(row.OrganizationPosition, 10)
            : null,
          CreatedBy: email,
          CreatedDate: now,
          UpdatedBy: email,
          UpdatedDate: now,
        }))
      : [
          {
            trxCandidatePersonalInformationID: null,
            Email: email,
            OrganizationName: '',
            mstOrganizationTypeID: null,
            OrganizationPosition: null,
            CreatedBy: email,
            CreatedDate: now,
            UpdatedBy: email,
            UpdatedDate: now,
          },
        ]
  form.append('trxCandidateOrganizationExperience', JSON.stringify(orgRows))

  form.append(
    'trxAchievementSkillCandidate',
    JSON.stringify({
      trxCandidatePersonalInformationID: null,
      Email: email,
      IsHaveAchievement: input.achievement.IsHaveAchievement,
      AchievementDescription: input.achievement.AchievementDescription || '',
      SoftSkillDescription: input.achievement.SoftSkillDescription || '',
      TechnicalSkill: input.achievement.TechnicalSkill || '',
      CertificationPath: '',
      CreatedBy: email,
      CreatedDate: now,
      UpdatedBy: null,
      UpdatedDate: null,
    }),
  )

  form.append(
    'trxFamilyAndRelativeCandidate',
    JSON.stringify({
      trxFamilyAndRelativeCandidateID: null,
      trxCandidatePersonalInformationID: null,
      Email: email,
      // An unanswered radio goes out as false — the value the pre-checked No
      // radio in the Razor would have sent.
      IsHaveFamilyInTBG: input.family.IsHaveFamilyInTBG === true,
      // Cleared when the answer is "No", as SaveAll did.
      FamilyInTBG: input.family.IsHaveFamilyInTBG ? input.family.FamilyInTBG.trim() : '',
      IsHaveFamilyInTelcoIndustry: input.family.IsHaveFamilyInTelcoIndustry === true,
      FamilyInTelcoIndustry: input.family.IsHaveFamilyInTelcoIndustry
        ? input.family.FamilyInTelcoIndustry.trim()
        : '',
    }),
  )

  form.append(
    'trxMedicalHistoryCandidate',
    JSON.stringify({
      trxMedicalHistoryCandidateID: null,
      trxCandidatePersonalInformationID: null,
      Email: email,
      IsHaveMedicalHistory: input.medical.IsHaveMedicalHistory === true,
      MedicalHistory: input.medical.MedicalHistory || '',
    }),
  )

  return form
}
